/* dental-health-checker.js — vanilla progressive enhancement for the Dental Health Checker page.
 * Replaces the framework runtime from the React prototype:
 *   1. the interactive checker (#sn-dental-health-checker [data-sn-widget="DentalChecker"])
 *      — pet toggle, gums, teeth, multi-select signs → live verdict + Nova handoff
 * The FAQ accordion is owned by the shared converted/_assets/sn-accordion.js.
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The page renders a faithful static snapshot server-side; this script enhances it.
 * If the script never runs, the static checker shell + FAQ questions are still visible.
 *
 * Ported verbatim from DentalChecker.jsx, DentalContent.jsx and the illustration components.
 */
(function () {
  'use strict';

  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false;
  var T = function (en, de) { return SN_DE ? de : en; };

  // ---- Checker data (ported verbatim from DentalChecker.jsx) ----------------
  var DC_GUMS = [
    { id: 'healthy', label: T('Healthy pink', 'Gesund rosa'), sub: T('Firm, no bleeding', 'Fest, blutet nicht'), base: 'fine' },
    { id: 'red', label: T('Red gum line', 'Roter Zahnfleischrand'), sub: T('Inflamed edge', 'Entzündeter Rand'), base: 'early' },
    { id: 'pale', label: T('Very pale', 'Sehr blass'), sub: T('Washed-out', 'Ausgeblichen'), base: 'soon' },
    { id: 'bleeding', label: T('Bleeding', 'Blutet'), sub: T('On gentle touch', 'Bei leichter Berührung'), base: 'soon' },
    { id: 'receding', label: T('Receding', 'Zurückgehend'), sub: T('Roots showing', 'Wurzeln sichtbar'), base: 'soon' },
  ];

  var DC_TEETH = [
    { id: 'white', label: T('White & clean', 'Weiß & sauber'), sub: T('No crust', 'Kein Belag'), base: 'fine' },
    { id: 'yellow', label: T('Yellow film', 'Gelber Film'), sub: T('Soft plaque', 'Weicher Plaque'), base: 'early' },
    { id: 'tartar', label: T('Brown tartar', 'Brauner Zahnstein'), sub: T('Hardened crust', 'Harter Belag'), base: 'soon' },
    { id: 'broken', label: T('Broken / cracked', 'Abgebrochen / gerissen'), sub: T('Chipped', 'Abgesplittert'), base: 'soon' },
    { id: 'loose', label: T('Loose / missing', 'Locker / fehlt'), sub: T('Wobbly or gone', 'Wackelt oder fehlt'), base: 'soon' },
    { id: 'grey', label: T('Grey / discoloured', 'Grau / verfärbt'), sub: T('Dead tooth?', 'Toter Zahn?'), base: 'soon' },
  ];

  var DC_SIGNS = [
    { id: 'breath', label: T('Bad breath', 'Übler Atem') },
    { id: 'drool', label: T('Drooling (or blood in it)', 'Speicheln (oder Blut darin)') },
    { id: 'paw', label: T('Pawing at the mouth', 'Pfoten am Maul') },
    { id: 'oneside', label: T('Chewing one side / dropping food', 'Kaut einseitig / lässt Futter fallen') },
    { id: 'eatless', label: T('Eating less', 'Frisst weniger') },
    { id: 'chatter', label: T('Chattering / grinding', 'Klappern / Knirschen') },
    { id: 'none', label: T('None of these', 'Keines davon') },
  ];

  var DC_RANK = { fine: 0, early: 1, soon: 2, now: 3 };
  var DC_VERDICT = {
    fine: { tag: T('Looks healthy', 'Sieht gesund aus'), headline: T('This looks like a healthy mouth.', 'Das sieht nach einem gesunden Maul aus.'), tone: '#5B8C3E', bg: '#E6FFC6', fg: '#224031' },
    early: { tag: T('Early signs', 'Erste Anzeichen'), headline: T('Early signs - worth booking a routine check.', 'Erste Anzeichen - ein Routinetermin lohnt sich.'), tone: '#E8B23A', bg: '#FFF3D4', fg: '#5A4413' },
    soon: { tag: T('Get seen soon', 'Zeitnah abklären'), headline: T('Worth getting this seen soon.', 'Das sollte zeitnah angesehen werden.'), tone: '#E07A3E', bg: '#FFE3CE', fg: '#7A3B16' },
    now: { tag: T('Call your vet now', 'Jetzt Tierarzt anrufen'), headline: T("Don't sit on this - call your vet today.", 'Warten Sie damit nicht - rufen Sie heute Ihre Tierarztpraxis an.'), tone: '#C5462E', bg: '#FBD9D0', fg: '#7A241A' },
  };

  function dcEvaluate(s) {
    var pet = s.pet, gums = s.gums, teeth = s.teeth, signs = s.signs;
    if (!gums && !teeth) return null;
    var reasons = [];
    var level = 'fine';
    var bump = function (lv, why) { if (DC_RANK[lv] > DC_RANK[level]) level = lv; if (why) reasons.push(why); };
    var has = function (id) { return signs.indexOf(id) !== -1; };

    // gums
    if (gums === 'healthy') reasons.push(T('Bubble-gum pink and firm is exactly what you want.', 'Kaugummirosa und fest ist genau richtig.'));
    if (gums === 'red') bump('early', T('A red gum line is gingivitis - inflamed, but still reversible with care.', 'Ein roter Zahnfleischrand ist eine Zahnfleischentzündung - entzündet, mit Pflege aber noch umkehrbar.'));
    if (gums === 'pale') bump('soon', T('Very pale gums can point to something systemic - worth a vet look.', 'Sehr blasses Zahnfleisch kann auf etwas Systemisches hindeuten - das gehört angesehen.'));
    if (gums === 'bleeding') bump('soon', T('Gums that bleed on gentle touch are a vet-soon flag.', 'Zahnfleisch, das bei leichter Berührung blutet, gehört zeitnah abgeklärt.'));
    if (gums === 'receding') bump('soon', T('Receding gums suggest periodontitis - the structures around the tooth breaking down.', 'Zurückgehendes Zahnfleisch deutet auf Parodontitis hin - das Gewebe um den Zahn baut sich ab.'));

    // teeth
    if (teeth === 'white') reasons.push(T('White, clean teeth with no crust at the gum line - textbook.', 'Weiße, saubere Zähne ohne Belag am Zahnfleischrand - wie im Lehrbuch.'));
    if (teeth === 'yellow') bump('early', T('Yellow film is soft plaque - reversible with daily cleaning.', 'Ein gelber Film ist weicher Plaque - mit täglicher Reinigung umkehrbar.'));
    if (teeth === 'tartar') bump('soon', T('Hardened brown tartar needs a professional scale and polish - you can\'t safely remove it at home.', 'Verhärteter brauner Zahnstein braucht eine professionelle Reinigung - zu Hause können Sie ihn nicht gefahrlos entfernen.'));
    if (teeth === 'broken') bump('soon', T('A broken or cracked tooth can be painful and exposed - get it checked.', 'Ein abgebrochener oder gerissener Zahn kann schmerzhaft und offen liegen - lassen Sie ihn ansehen.'));
    if (teeth === 'loose') bump('soon', T('A loose or missing adult tooth is a clear vet visit.', 'Ein lockerer oder fehlender erwachsener Zahn ist ein klarer Fall für die Praxis.'));
    if (teeth === 'grey') bump('soon', T('A grey or discoloured tooth can be dying - worth a vet assessment.', 'Ein grauer oder verfärbter Zahn kann absterben - das sollte beurteilt werden.'));

    // signs
    if (has('breath')) bump('early', T('"Dog breath" is a myth - genuinely bad breath is the most common early sign of dental disease.', '„Hundeatem“ ist ein Mythos - wirklich schlechter Atem ist das häufigste Frühzeichen einer Zahnerkrankung.'));
    if (has('drool')) bump('soon', T('Drooling, especially blood-tinged, points to a sore mouth.', 'Speicheln, besonders blutig, deutet auf ein wundes Maul hin.'));
    if (has('paw')) bump('early', T('Pawing at the mouth usually means something in there is bothering them.', 'Pfoten am Maul heißt meist, dass dort etwas stört.'));
    if (has('oneside')) bump('early', T('Chewing on one side or dropping food is a quiet sign of mouth pain.', 'Einseitiges Kauen oder Futter fallen lassen ist ein leises Zeichen für Schmerzen im Maul.'));
    if (has('eatless')) bump('soon', T('Eating less is often a sore mouth - pets hide dental pain well.', 'Weniger fressen bedeutet oft ein wundes Maul - Tiere verbergen Zahnschmerzen gut.'));
    if (has('chatter')) bump('early', T('New or frequent chattering / grinding can be a pain or dental response.', 'Neues oder häufiges Klappern bzw. Knirschen kann eine Reaktion auf Schmerzen oder ein Zahnproblem sein.'));

    // escalating combinations → vet now
    var badTooth = teeth === 'broken' || teeth === 'loose';
    if (badTooth && (gums === 'bleeding' || has('eatless'))) bump('now', T('A damaged tooth alongside bleeding or a drop in appetite needs a same-day call.', 'Ein beschädigter Zahn zusammen mit Blutung oder weniger Appetit gehört noch am selben Tag angerufen.'));
    if (gums === 'bleeding' && has('drool')) bump('now', T('Bleeding gums with drooling suggests an actively painful mouth - ring your vet.', 'Blutendes Zahnfleisch mit Speicheln deutet auf ein akut schmerzendes Maul hin - rufen Sie Ihre Praxis an.'));

    if (level === 'fine' && reasons.length === 0) reasons.push(T('Nothing here is raising a flag.', 'Hier fällt nichts auf.'));
    return { level: level, reasons: reasons };
  }

  // ---- SVG fragments (ported from the illustration components) --------------
  // DWorryDot — coloured status dot
  function worryDot(level, size) {
    var map = { fine: '#5B8C3E', early: '#E8B23A', soon: '#E07A3E', now: '#C5462E' };
    return '<span style="width:' + size + 'px;height:' + size + 'px;border-radius:999px;background:' +
      map[level] + ';display:inline-block;flex-shrink:0"></span>';
  }

  // Icon — lucide-style stroke icon
  function icon(name, size, color) {
    var paths = {
      arrow: '<path d="M5 12h14M13 6l6 6-6 6"></path>',
      check: '<path d="M20 6 9 17l-5-5"></path>',
    };
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + paths[name] + '</svg>';
  }

  // NovaOrb (AI vet avatar)
  function novaOrb(size) {
    var ring = '#CFFF90', face = '#224031';
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 120 120" fill="none" aria-hidden="true">' +
      '<circle cx="60" cy="60" r="56" fill="' + face + '"></circle>' +
      '<circle cx="60" cy="60" r="56" fill="none" stroke="' + ring + '" stroke-width="3" stroke-dasharray="6 9" opacity="0.7"></circle>' +
      '<circle cx="44" cy="54" r="7" fill="' + ring + '"></circle>' +
      '<circle cx="76" cy="54" r="7" fill="' + ring + '"></circle>' +
      '<circle cx="46" cy="56" r="2.6" fill="' + face + '"></circle>' +
      '<circle cx="78" cy="56" r="2.6" fill="' + face + '"></circle>' +
      '<path d="M44 74c5 6 27 6 32 0" stroke="' + ring + '" stroke-width="5" stroke-linecap="round" fill="none"></path>' +
      '<path d="M92 30l2.4 6.4L101 39l-6.6 2.4L92 48l-2.4-6.6L83 39l6.6-2.6Z" fill="' + ring + '"></path></svg>';
  }

  // GumSwatch — gum arch with a few teeth, styled by state
  function gumSwatch(state, size) {
    var ink = '#224031';
    var gumColor = { healthy: '#F2A6AE', red: '#E2727C', pale: '#F6D9DC', bleeding: '#D85563', receding: '#F2A6AE' }[state];
    var s = '<svg width="' + size + '" height="' + (size * 0.82) + '" viewBox="0 0 60 50" fill="none" aria-hidden="true">';
    s += '<path d="M4 8c0-3 52-3 52 0v14c0 8-52 8-52 0V8Z" fill="' + gumColor + '"></path>';
    if (state === 'red') s += '<rect x="4" y="20" width="52" height="6" rx="3" fill="#B83A44"></rect>';
    if (state === 'receding') s += '<path d="M4 22c0 8 52 8 52 0" stroke="#C77" stroke-width="3" fill="none" stroke-dasharray="3 4"></path>';
    s += '<g fill="#fff" stroke="' + ink + '" stroke-width="1.6">';
    var tx = state === 'receding' ? 'translate(0 -4)' : '';
    [12, 23, 34, 45].forEach(function (x) {
      s += '<path d="M' + x + ' 22c-4 0-6 4-5 9 .5 4 1.5 9 5 9s4.5-5 5-9c1-5-1-9-5-9Z" transform="' + tx + '"></path>';
    });
    s += '</g>';
    if (state === 'bleeding') s += '<path d="M50 30c0 4-5 4-5 0 0-2 2.5-5 2.5-5S50 28 50 30Z" fill="#C5462E"></path>';
    s += '</svg>';
    return s;
  }

  // ToothSwatch — a single tooth, styled by state
  function toothSwatch(state, size) {
    var ink = '#224031';
    var crown = function (fill) {
      return '<path d="M24 6c-9-6-22-3-23 8-1 8 3 14 5 22 1 6 2 13 6 13 3 0 3-6 4-10 1-3 2-5 4-5s3 2 4 5c1 4 1 10 4 10 4 0 5-7 6-13 2-8 6-14 5-22C38 3 33 0 24 6Z" fill="' +
        fill + '" stroke="' + ink + '" stroke-width="2.4" stroke-linejoin="round"></path>';
    };
    var s = '<svg width="' + size + '" height="' + (size * 1.15) + '" viewBox="0 0 48 56" fill="none" aria-hidden="true">';
    if (state === 'white') s += crown('#fff');
    if (state === 'yellow') s += crown('#F4E8C8') + '<path d="M14 16c6-3 14-3 20 0" stroke="#D9C27E" stroke-width="3" stroke-linecap="round"></path>';
    if (state === 'grey') s += crown('#C6C9CA');
    if (state === 'tartar') s += crown('#fff') + '<path d="M8 30c4 4 28 4 32 0-1 8-3 14-6 14-3 0-3-6-4-10-1-3-2-5-4-5s-3 2-4 5c-1 4-1 10-4 10-3 0-5-6-6-14Z" fill="#9C6B2F"></path>';
    if (state === 'broken') s += crown('#fff') + '<path d="M24 5l-5 13 7 6-5 9" stroke="' + ink + '" stroke-width="2.6" fill="none" stroke-linejoin="round"></path>';
    if (state === 'loose') s += '<g transform="rotate(12 24 30)">' + crown('#fff') + '<circle cx="40" cy="10" r="3" fill="#C5462E"></circle></g>';
    s += '</svg>';
    return s;
  }

  // ---- Tiny DOM helper ------------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var a in opts.attrs) e.setAttribute(a, opts.attrs[a]);
    (kids || []).forEach(function (c) {
      if (c == null) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  // selection style (ported from `sel(active)` in DentalChecker.jsx)
  function selCss(active) {
    return 'border:' + (active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)') + ';' +
      'background:' + (active ? 'var(--sn-lime-soft)' : '#fff') + ';' +
      'box-shadow:' + (active ? '0 0 0 4px rgba(207,255,144,0.4)' : 'none');
  }

  // step header (DC_StepHeader)
  function stepHeader(n, title, hint) {
    return h('div', { css: 'display:flex;align-items:baseline;gap:12px;margin-bottom:16px' }, [
      h('span', { css: 'font-family:var(--sn-display);font-weight:700;font-size:13px;color:var(--sn-forest);' +
          'background:var(--sn-lime-soft);border-radius:999px;width:26px;height:26px;display:inline-flex;' +
          'align-items:center;justify-content:center;flex-shrink:0' }, [String(n)]),
      h('div', {}, [
        h('h3', { css: 'font-size:21px;line-height:1.1' }, [title]),
        hint ? h('span', { css: 'font-size:13.5px;color:var(--sn-ink-60)' }, [hint]) : null,
      ]),
    ]);
  }

  // ---- Checker widget -------------------------------------------------------
  function initChecker(root) {
    var state = { pet: 'dog', gums: null, teeth: null, signs: [] };

    function toggleSign(id) {
      var prev = state.signs;
      if (id === 'none') { state.signs = prev.indexOf('none') !== -1 ? [] : ['none']; return; }
      var next = prev.filter(function (s) { return s !== 'none'; });
      state.signs = next.indexOf(id) !== -1 ? next.filter(function (s) { return s !== id; }) : next.concat([id]);
    }

    function novaQuestion(result) {
      if (!result) return '';
      var pet = state.pet, gums = state.gums, teeth = state.teeth, signs = state.signs;
      var find = function (arr, id) { return arr.filter(function (x) { return x.id === id; })[0]; };
      var q = "My " + pet + "'s gums look " + (gums ? find(DC_GUMS, gums).label.toLowerCase() : 'normal');
      if (teeth) q += ' and their teeth are ' + find(DC_TEETH, teeth).label.toLowerCase();
      if (signs.length && signs.indexOf('none') === -1) {
        q += ', with ' + signs.map(function (s) { return find(DC_SIGNS, s).label.toLowerCase(); }).join(' and ');
      }
      q += '. Should I worry?';
      return q;
    }

    // --- Verdict panel (right column) ---
    function renderVerdict() {
      var panel = root.querySelector('[data-dc-verdict]');
      var result = dcEvaluate(state);
      var v = result ? DC_VERDICT[result.level] : null;
      panel.setAttribute('data-dc-level', result ? result.level : 'empty');
      panel.style.cssText = 'background-color:' + (v ? v.bg : '#224031') + ';color:' + (v ? v.fg : 'var(--sn-cream)') +
        ';padding:34px 32px;display:flex;flex-direction:column';
      panel.innerHTML = '';

      if (!result) {
        panel.appendChild(h('div', { css: 'margin:auto;text-align:center;max-width:260px' }, [
          h('div', { html: novaOrb(92) }),
          h('h3', { css: 'font-size:22px;margin:18px 0 8px;color:var(--sn-cream)' }, ['Lift the lip and tell me what you see']),
          h('p', { css: 'font-size:14.5px;color:rgba(255,248,240,0.72);line-height:1.55' },
            [T('Start with the gums or teeth to get a plain-English read - and what to do next.', 'Beginnen Sie mit dem Zahnfleisch oder den Zähnen - Sie bekommen eine klare Einschätzung und wissen, was als Nächstes zu tun ist.')]),
        ]));
        return;
      }

      var novaQ = novaQuestion(result);
      var wrap = h('div', { css: 'display:flex;flex-direction:column;height:100%' }, [
        h('span', { cls: 'sn-eyebrow', css: 'color:' + v.tone + ';margin-bottom:12px',
          html: worryDot(result.level, 12) + ' ' }, [v.tag]),
        h('h3', { css: 'font-size:27px;line-height:1.12;margin-bottom:16px' }, [v.headline]),
        h('ul', { css: 'list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:12px' },
          result.reasons.map(function (r) {
            return h('li', { css: 'display:flex;gap:10px;font-size:14.5px;line-height:1.5' }, [
              h('span', { css: 'margin-top:6px;flex-shrink:0', html: worryDot(result.level, 7) }),
              h('span', {}, [r]),
            ]);
          })),
        h('div', { css: 'margin-top:auto;padding-top:24px' }, [
          h('div', { css: 'background:rgba(255,255,255,0.55);border-radius:16px;padding:16px 18px;margin-bottom:14px;' +
              'border:1px solid ' + v.tone + '33' }, [
            h('div', { css: 'display:flex;align-items:center;gap:8px;margin-bottom:8px' }, [
              h('div', { html: novaOrb(30) }),
              h('strong', { css: 'font-family:var(--sn-display);font-size:14.5px' }, [T("Ask Nova, with your pet's profile", 'Nova fragen - mit dem Profil Ihres Tieres')]),
            ]),
            h('p', { attrs: { 'data-dc-novaq': '' }, css: 'font-size:13px;font-style:italic;line-height:1.45;opacity:0.85' }, ['"' + novaQ + '"']),
          ]),
          h('a', { cls: 'sn-btn sn-btn--primary', css: 'width:100%;justify-content:center',
            html: T('Ask Nova about this ', 'Nova dazu fragen ') + icon('arrow', 18, 'currentColor') }),
          h('p', { css: 'font-size:11.5px;text-align:center;margin-top:12px;opacity:0.62' },
            ['Not a diagnosis. If your pet has facial swelling or has stopped eating, call your vet.']),
        ]),
      ]);
      panel.appendChild(wrap);
    }

    // --- sync the control selections (left column) without rebuilding ---
    function syncControls() {
      root.querySelectorAll('[data-dc-pet]').forEach(function (btn) {
        var active = btn.getAttribute('data-dc-pet') === state.pet;
        btn.style.background = active ? 'var(--sn-forest)' : 'transparent';
        btn.style.color = active ? 'var(--sn-cream)' : 'var(--sn-ink-60)';
      });
      root.querySelectorAll('[data-dc-gum]').forEach(function (btn) {
        btn.style.cssText = 'cursor:pointer;border-radius:16px;padding:14px 8px;text-align:center;display:flex;' +
          'flex-direction:column;align-items:center;gap:6px;' + selCss(btn.getAttribute('data-dc-gum') === state.gums);
      });
      root.querySelectorAll('[data-dc-tooth]').forEach(function (btn) {
        btn.style.cssText = 'cursor:pointer;border-radius:16px;padding:14px 8px;text-align:center;display:flex;' +
          'flex-direction:column;align-items:center;gap:5px;' + selCss(btn.getAttribute('data-dc-tooth') === state.teeth);
      });
      root.querySelectorAll('[data-dc-sign]').forEach(function (btn) {
        var id = btn.getAttribute('data-dc-sign');
        var active = state.signs.indexOf(id) !== -1;
        btn.style.cssText = 'cursor:pointer;border-radius:999px;padding:11px 18px;font-size:14.5px;font-weight:500;' +
          'font-family:var(--sn-body);display:inline-flex;align-items:center;gap:8px;' +
          'border:' + (active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)') + ';' +
          'background:' + (active ? 'var(--sn-forest)' : '#fff') + ';' +
          'color:' + (active ? 'var(--sn-cream)' : 'var(--sn-ink)');
        btn.innerHTML = (active ? icon('check', 15, 'var(--sn-lime)') : '') + DC_SIGNS.filter(function (x) { return x.id === id; })[0].label;
      });
    }

    function update() { syncControls(); renderVerdict(); }

    // --- Build the interactive controls (left column) ---
    var petToggle = h('div', { css: 'display:flex;gap:8px;background:var(--sn-cream-2);border-radius:999px;padding:5px;width:fit-content;margin-bottom:30px' },
      ['dog', 'cat'].map(function (p) {
        return h('button', {
          attrs: { 'data-dc-pet': p },
          css: 'border:none;cursor:pointer;border-radius:999px;padding:9px 26px;font-family:var(--sn-display);' +
            'font-weight:600;font-size:15px;text-transform:capitalize;' +
            'background:' + (state.pet === p ? 'var(--sn-forest)' : 'transparent') + ';' +
            'color:' + (state.pet === p ? 'var(--sn-cream)' : 'var(--sn-ink-60)'),
          on: { click: function () { state.pet = p; update(); } },
        }, [p]);
      }));

    var gumsGrid = h('div', { css: 'display:grid;grid-template-columns:repeat(3,1fr);gap:11px;margin-bottom:32px' },
      DC_GUMS.map(function (g) {
        return h('button', {
          attrs: { 'data-dc-gum': g.id },
          css: 'cursor:pointer;border-radius:16px;padding:14px 8px;text-align:center;display:flex;' +
            'flex-direction:column;align-items:center;gap:6px;' + selCss(state.gums === g.id),
          on: { click: function () { state.gums = g.id; update(); } },
        }, [
          h('span', { html: gumSwatch(g.id, 50) }),
          h('span', { css: 'font-family:var(--sn-display);font-weight:600;font-size:13.5px;line-height:1.1' }, [g.label]),
          h('span', { css: 'font-size:11px;color:var(--sn-ink-60)' }, [g.sub]),
        ]);
      }));

    var teethGrid = h('div', { css: 'display:grid;grid-template-columns:repeat(3,1fr);gap:11px;margin-bottom:32px' },
      DC_TEETH.map(function (t) {
        return h('button', {
          attrs: { 'data-dc-tooth': t.id },
          css: 'cursor:pointer;border-radius:16px;padding:14px 8px;text-align:center;display:flex;' +
            'flex-direction:column;align-items:center;gap:5px;' + selCss(state.teeth === t.id),
          on: { click: function () { state.teeth = t.id; update(); } },
        }, [
          h('span', { html: toothSwatch(t.id, 38) }),
          h('span', { css: 'font-family:var(--sn-display);font-weight:600;font-size:13.5px;line-height:1.1' }, [t.label]),
          h('span', { css: 'font-size:11px;color:var(--sn-ink-60)' }, [t.sub]),
        ]);
      }));

    var signsRow = h('div', { css: 'display:flex;flex-wrap:wrap;gap:9px' },
      DC_SIGNS.map(function (s) {
        var active = state.signs.indexOf(s.id) !== -1;
        return h('button', {
          attrs: { 'data-dc-sign': s.id },
          css: 'cursor:pointer;border-radius:999px;padding:11px 18px;font-size:14.5px;font-weight:500;' +
            'font-family:var(--sn-body);display:inline-flex;align-items:center;gap:8px;' +
            'border:' + (active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)') + ';' +
            'background:' + (active ? 'var(--sn-forest)' : '#fff') + ';' +
            'color:' + (active ? 'var(--sn-cream)' : 'var(--sn-ink)'),
          html: (active ? icon('check', 15, 'var(--sn-lime)') : '') + s.label,
          on: { click: function () { toggleSign(s.id); update(); } },
        });
      }));

    var controls = h('div', { css: 'padding:34px 38px' }, [
      petToggle,
      stepHeader(1, 'How do the gums look?', 'Lift the lip and have a look along the gum line'),
      gumsGrid,
      stepHeader(2, 'What about the teeth?', null),
      teethGrid,
      stepHeader(3, T('Any other signs?', 'Weitere Anzeichen?'), T('Select all that apply', 'Alles Zutreffende auswählen')),
      signsRow,
    ]);

    var verdict = h('div', { attrs: { 'data-dc-verdict': '' } });

    var grid = h('div', { css: 'display:grid;grid-template-columns:1.35fr 1fr;border-radius:28px;overflow:hidden;' +
        'border:1px solid var(--sn-line);box-shadow:0 30px 70px -45px rgba(28,25,23,0.5);background:#fff' }, [controls, verdict]);

    root.innerHTML = '';
    root.appendChild(grid);
    update();
  }

  // The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
  // Answers are rendered into the static body.html (for SEO/crawlability), so this
  // file only owns the DentalChecker widget.

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var page = document.getElementById('sn-dental-health-checker');
    if (!page) return;
    var widget = page.querySelector('[data-sn-widget="DentalChecker"]');
    if (widget) initChecker(widget);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
